/**
 * @file ShopCabinetEntry.tsx
 * @description «Кабинет ↗» в карточке магазина — вход под владельцем с
 * причиной (phase19.28 этап А; PL17, В9; impersonation.md «Вход в кабинет
 * магазина»).
 *
 * Окно входа (`ImpersonationStartModal`) нужно с полной записью
 * пользователя (его кабинеты), поэтому при открытии читаем владельца
 * `GET /admin/users/:id` (а не весь список); вход — только в кабинет этого
 * магазина (`shopId`). Кнопку показывают при правах
 * PLATFORM_IMPERSONATE_USER и PLATFORM_MANAGE_USERS. После старта — перезагрузка кабинета,
 * как у входа со страницы «Пользователи».
 */

import { useEffect, useState } from "react";

import { getAdminUser, type AdminUser } from "../../../lib/api-admin-users";
import { apiErrorText } from "../../../lib/platform/shop-errors";
import { ImpersonationStartModal } from "../../ImpersonationStartModal";
import { useNotify } from "../../ui/NotifyProvider";

interface Props {
  /** Кого открыть: id владельца; null — окно закрыто. */
  ownerId: string | null;
  shopId: string;
  onClose: () => void;
}

export function ShopCabinetEntry({ ownerId, shopId, onClose }: Props) {
  const notify = useNotify();
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    if (!ownerId) return;
    let alive = true;
    getAdminUser(ownerId)
      .then((found) => {
        if (alive) setUser(found);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        notify.error(apiErrorText(err, "Не удалось открыть вход в кабинет: владелец не найден"));
        onClose();
      });
    return () => {
      alive = false;
    };
  }, [ownerId, notify, onClose]);

  if (!ownerId || !user || user.id !== ownerId) return null;
  return (
    <ImpersonationStartModal
      open
      user={user}
      shopId={shopId}
      onClose={() => {
        setUser(null);
        onClose();
      }}
      onStarted={() => window.location.assign(import.meta.env.BASE_URL)}
    />
  );
}
